import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

/**
 * Internal smooth-reveal engine — NOT part of the `@qw/chat` public surface (this
 * module is deliberately absent from `hooks/index.ts`). The public reveal API is the
 * two adapter hooks: `useSmoothStreamedText` (single string) and
 * `useSequentialPartReveal` (multi-part), which both drive this engine. Keeping the
 * 5-input `useSmoothRevealCursor` state-machine contract here means callers can't
 * couple to it before it stabilizes.
 */

/**
 * Position lag time constant, in milliseconds. The reveal targets a rate that
 * would clear the current backlog in this long, so at steady input it settles
 * to a constant velocity while holding a ~`POSITION_TAU_MS` buffer behind the
 * incoming text — big enough that a single chunk arriving barely moves the
 * target rate, which is what keeps chunk seams from showing.
 */
const POSITION_TAU_MS = 300;

/**
 * Velocity smoothing time constant, in milliseconds. The reveal *rate* is
 * low-pass filtered toward its target with this constant, so the velocity ramps
 * gradually instead of jumping when a chunk lands. This is what removes the
 * per-chunk velocity discontinuity (the visible seam) — the reveal keeps moving
 * at a near-constant speed across chunk boundaries.
 */
const VELOCITY_TAU_MS = 120;

/**
 * Maximum times per second the revealed text is committed to React (and the
 * Markdown reparsed). rAF fires at the refresh rate — up to 120/s on ProMotion
 * — but we commit at most this often. 60/s sub-divides the ~33ms chunk cadence
 * (so a chunk reveals over multiple frames rather than one step) while capping
 * Markdown reparses on long messages.
 */
const MAX_RENDER_FPS = 60;
const MIN_RENDER_INTERVAL_MS = 1000 / MAX_RENDER_FPS;

interface SmoothRevealState {
  /** Characters revealed so far (fractional; the render floors it). */
  revealed: number;
  /** Current reveal velocity, in characters per second. */
  rate: number;
}

interface ComputeSmoothRevealArgs extends SmoothRevealState {
  /** Total characters available to reveal (`fullText.length`). */
  target: number;
  /** Milliseconds elapsed since the last step. */
  elapsedMs: number;
  /** Position lag constant (ms); see `POSITION_TAU_MS`. */
  positionTauMs: number;
  /** Velocity smoothing constant (ms); see `VELOCITY_TAU_MS`. */
  velocityTauMs: number;
  /**
   * Start offset of the segment whose first character the prompt carve-out should
   * guarantee. `0` for a single-string reveal (prompt the very first character);
   * the current part-boundary offset for a multi-part reveal, so the guarantee
   * re-arms at each part boundary rather than only at the global start. Default 0.
   */
  promptOriginChars?: number;
}

/**
 * Pure velocity-smoothed reveal step. Given the current revealed position and
 * velocity, the target length, and the elapsed time, return the next position
 * and velocity.
 *
 * Model (a critically-damped-ish tracker):
 * - Caught up / overshot → snap to `target`, velocity 0.
 * - Target velocity = `backlog / positionTau` (chars/s) — the speed that would
 *   clear the current backlog in `positionTau`.
 * - Actual velocity eases toward the target velocity (an EMA with
 *   `velocityTau`), so a chunk arriving (which bumps the backlog, hence the
 *   target velocity) only nudges the *actual* velocity gradually — no seam.
 * - Advance position by `velocity × elapsed`, clamped to `target`.
 *
 * For steady input this converges to a constant velocity equal to the input
 * rate, trailing by a fixed ~`positionTau` buffer — a seamless typewriter that
 * ignores per-chunk jitter. Extracted so the cadence math is deterministically
 * unit-testable without a DOM / `requestAnimationFrame`.
 *
 * Prompt carve-out: `targetRate` scales with backlog, so a tiny backlog
 * (1–2 chars) ramps from velocity ~0 slowly enough that the text can sit blank
 * for a few hundred ms. When the cursor is at a segment's start (`< promptOriginChars + 1`),
 * force the first character out immediately so a segment never appears to stall — at the
 * global start (`promptOriginChars = 0`), and at each part boundary in a multi-part reveal
 * (where the cursor catches up to a boundary with rate ≈ 0 as the next part's content arrives).
 * Past the first character the normal velocity smoothing takes over.
 */
export const computeSmoothReveal = ({
  revealed,
  rate,
  target,
  elapsedMs,
  positionTauMs,
  velocityTauMs,
  promptOriginChars = 0,
}: ComputeSmoothRevealArgs): SmoothRevealState => {
  if (revealed >= target) {
    return { revealed: target, rate: 0 };
  }

  const backlog = target - revealed;
  const targetRate = backlog / (positionTauMs / 1000);
  const alpha = Math.min(1, elapsedMs / velocityTauMs);
  const nextRate = rate + (targetRate - rate) * alpha;
  let nextRevealed = Math.min(target, revealed + (nextRate * elapsedMs) / 1000);
  // Prompt the active segment's first character (see doc above). `promptFloor` is that
  // character's position; below it, snap the cursor forward so the segment never sits blank.
  const promptFloor = promptOriginChars + 1;
  if (revealed < promptFloor && nextRevealed < promptFloor) {
    nextRevealed = Math.min(target, promptFloor);
  }
  return { revealed: nextRevealed, rate: nextRate };
};

/**
 * The canonical smooth-reveal engine — the single owner of the reveal lifecycle
 * (reduced-motion gate, cursor/rate/draining refs, the rAF loop with its commit
 * cap, cleanup, and idle-sync). Both `useSmoothStreamedText` (single string) and
 * `useSequentialPartReveal` (multi-part) are thin adapters over this, so drain /
 * motion / mount semantics live in exactly one place.
 *
 * The loop is keyed only on `[active, reducedMotion]` and reads `target` / `promptOriginFor`
 * from refs, so a growing target (a new chunk ~30×/s) does NOT tear down and restart the loop —
 * position and velocity carry across chunk boundaries and the animate→drain transition. Commits
 * (hence downstream re-renders / Markdown reparses) are capped at `MAX_RENDER_FPS`.
 *
 * Behavior:
 * - Not animating at mount (historical / finished, or reduced-motion) → seeds at `total` (lands
 *   instantly).
 * - Animating at mount → seeds at `initialCursor` (0 for a fresh stream; past already-complete
 *   leading segments on a re-attach).
 * - **Drains to completion:** once under way it finishes even if `active` flips false mid-reveal
 *   (e.g. a terminal reasoning tail whose message settles first); it never snaps the tail to full.
 *
 * @returns the committed reveal cursor (an integer count of characters); callers slice with it.
 */
export const useSmoothRevealCursor = ({
  active,
  target,
  total,
  initialCursor,
  promptOriginFor,
}: {
  /** Whether the content is the actively-streaming turn (drives animate, with reduced-motion). */
  active: boolean;
  /** Characters currently eligible to reveal (may be < `total` while later content is gated). */
  target: number;
  /** Full character count (mount seed when not animating, and the idle-sync target). */
  total: number;
  /** Cursor to seed when animating at mount. */
  initialCursor: number;
  /** Maps the current cursor to the active segment's start offset for the prompt carve-out. */
  promptOriginFor?: (revealed: number) => number;
}): number => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const animate = active && !reducedMotion;

  const [cursor, setCursor] = useState(() => (animate ? initialCursor : total));

  // Mirrors read by the rAF loop so it sees the latest values without re-subscribing every chunk.
  // `rateRef` carries velocity across the animate→drain transition so the tail doesn't re-ramp.
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const targetRef = useRef(target);
  targetRef.current = target;
  const totalRef = useRef(total);
  totalRef.current = total;
  const promptOriginForRef = useRef(promptOriginFor);
  promptOriginForRef.current = promptOriginFor;
  const rateRef = useRef(0);
  const drainingRef = useRef(false);

  useEffect(() => {
    if (reducedMotion) {
      drainingRef.current = false;
      cursorRef.current = totalRef.current;
      rateRef.current = 0;
      setCursor(totalRef.current);
      return;
    }

    // Not streaming and already caught up (historical / settled / idle): no loop; the idle-sync
    // effect keeps the cursor tracking `total`.
    if (!animate && cursorRef.current >= targetRef.current) {
      drainingRef.current = false;
      return;
    }

    // Live reveal (`animate`) or a drain (`!animate` with backlog left). Seed from the refs so
    // position/velocity carry across chunk arrivals AND the animate→drain transition.
    drainingRef.current = !animate;
    let frame: number | null = null;
    let lastTs: number | null = null;
    let revealed = cursorRef.current;
    let rate = rateRef.current;
    let committed = Math.floor(revealed);

    const step = (ts: number) => {
      if (lastTs === null) {
        lastTs = ts;
      }
      const elapsedMs = ts - lastTs;

      if (elapsedMs >= MIN_RENDER_INTERVAL_MS) {
        const next = computeSmoothReveal({
          revealed,
          rate,
          target: targetRef.current,
          elapsedMs,
          positionTauMs: POSITION_TAU_MS,
          velocityTauMs: VELOCITY_TAU_MS,
          promptOriginChars: promptOriginForRef.current?.(revealed) ?? 0,
        });
        revealed = next.revealed;
        rate = next.rate;
        rateRef.current = rate;
        lastTs = ts;

        const nextCount = Math.floor(revealed);
        if (nextCount !== committed) {
          committed = nextCount;
          cursorRef.current = nextCount;
          setCursor(nextCount);
        }
      }

      if (animate || revealed < targetRef.current) {
        frame = requestAnimationFrame(step);
      } else {
        drainingRef.current = false;
        frame = null;
      }
    };

    frame = requestAnimationFrame(step);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, [animate, reducedMotion]);

  // Idle-sync: when NOT revealing (not streaming, not draining), the cursor must track the full
  // length — reduced-motion, historical messages, and settled replies whose refetch grows the text
  // while idle. Keeps the cursor correct-by-construction instead of clamped to a stale value.
  useEffect(() => {
    if (!animate && !drainingRef.current && cursorRef.current !== total) {
      cursorRef.current = total;
      setCursor(total);
    }
  }, [total, animate]);

  return cursor;
};
